import { createFileRoute } from '@tanstack/react-router'
import {
  type EvaluationFeedbackData,
  PitchEvaluationView,
} from '@workspace/seedforge-ui/evaluation'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { FounderLayout } from '../../../../components/founder-layout'
import { VideoPreviewModal } from '../../../../components/video-preview-modal'
import { ContactMessageDialog } from '../../../../features/evaluation/components/contact-message-dialog'
import { InvestorInfoCard } from '../../../../features/evaluation/components/investor-info-card'
import { DocumentPreviewModal } from '../../../../features/preview/components/document-preview-modal'
import {
  useCreateFeedbackMutation,
  useGetDocumentDownloadUrlMutation,
  usePitchEvaluationQuery,
  usePitchVideoUrlMutation,
  useStartupOutreachBodyQuery,
} from '../../../../hooks/api'
import { useBasePath } from '../../../../hooks/use-base-path'
import { useDocumentPreview } from '../../../../hooks/use-document-preview'
import { useVideoPreview } from '../../../../hooks/use-video-preview'

export const Route = createFileRoute('/app/startup/$id/evaluation/$evaluationId')({
  component: EvaluationPage,
})

function EvaluationPage() {
  const { id: startupId, evaluationId } = Route.useParams()
  const basePath = useBasePath()
  const evaluationQuery = usePitchEvaluationQuery(startupId, evaluationId)
  const outreachBodyQuery = useStartupOutreachBodyQuery(startupId)
  const [savedOutreachBody, setSavedOutreachBody] = useState<string | null>(null)
  const [hooksByContact, setHooksByContact] = useState<Record<string, string>>({})
  const [contactDialog, setContactDialog] = useState<{
    open: boolean
    contact: {
      id: string
      name: string
      title: string | null
      linkedinUrl: string | null
      scope: string | null
      order: number
    } | null
  }>({ open: false, contact: null })
  const feedbackMutation = useCreateFeedbackMutation()
  const videoUrlMutation = usePitchVideoUrlMutation()
  const docDownloadMutation = useGetDocumentDownloadUrlMutation()
  const docPreview = useDocumentPreview({
    fetchDocumentUrl: async (documentId) => {
      const res = await docDownloadMutation.mutateAsync({ path: { id: documentId } })
      if (res.status === 'ok') return res.data.downloadUrl
      throw new Error('Failed to get download URL')
    },
  })

  const pitchSessionId = evaluationQuery.data?.data?.pitchSessionId
  const pitchDeckId = evaluationQuery.data?.data?.pitchDeckId

  const video = useVideoPreview({
    fetchId: pitchSessionId,
    fetchVideoUrl: (id) =>
      videoUrlMutation.mutateAsync({ path: { pitchId: id } }).then((r) => r.data.url),
  })

  const handleFeedbackSubmit = (feedbackData: EvaluationFeedbackData) => {
    feedbackMutation.mutate({
      body: {
        message: feedbackData.comment,
        vote: feedbackData.vote ?? undefined,
        rating: feedbackData.rating ?? undefined,
        evaluationId,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      },
    })
  }

  const handleOpenPitchDeck = () => {
    if (!pitchDeckId) return
    docPreview.openDocument({
      id: pitchDeckId,
      fileName: 'Pitch Deck',
      mimeType: 'application/pdf',
    })
  }

  if (evaluationQuery.isLoading) {
    return (
      <FounderLayout activeNav="">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </FounderLayout>
    )
  }

  if (evaluationQuery.error || !evaluationQuery.data?.data) {
    return (
      <FounderLayout activeNav="">
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Failed to load evaluation
        </div>
      </FounderLayout>
    )
  }

  const evaluation = evaluationQuery.data.data

  const logContactClick = (investorId: string) => {
    void fetch(
      `${import.meta.env.VITE_BACKEND_URL}/api/v2/startups/${startupId}/matches/${investorId}/contact`,
      { method: 'POST', credentials: 'include' }
    ).catch(() => {
      /* best-effort */
    })
  }

  const handleContactInvestor = (contact: {
    id: string
    name: string
    title: string | null
    linkedinUrl: string | null
    scope: string | null
    order: number
  }) => {
    if (!evaluation.investor) return
    setContactDialog({ open: true, contact })
  }
  const investorCard = evaluation.investor ? (
    <InvestorInfoCard
      name={evaluation.investor.name}
      logoUrl={evaluation.investor.logoUrl}
      description={evaluation.investor.description}
      geographies={evaluation.investor.investmentGeographies}
      sectors={evaluation.investor.investmentAreas}
      stages={evaluation.investor.investmentStages}
      investorType={evaluation.investor.investorType}
      minTicketSize={evaluation.investor.minTicketSize}
      maxTicketSize={evaluation.investor.maxTicketSize}
      websiteUrl={evaluation.investor.websiteUrl ?? null}
      linkedinUrl={evaluation.investor.linkedinUrl ?? null}
      recentPortfolios={evaluation.investor.recentPortfolios ?? []}
      contacts={evaluation.investor.contacts ?? []}
      onContact={handleContactInvestor}
    />
  ) : null

  const dateStr = new Date(evaluation.date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <FounderLayout activeNav="">
      <PitchEvaluationView
        title={evaluation.title}
        sessionLabel={evaluation.sessionLabel}
        date={dateStr}
        pitchDuration={evaluation.pitchDuration}
        summary={evaluation.summary}
        score={evaluation.score}
        metrics={evaluation.metrics.map((m) => ({ ...m, delta: m.delta ?? undefined }))}
        winnerJudge={evaluation.winnerJudge}
        judges={evaluation.judges}
        metricBreakdowns={evaluation.metricBreakdowns}
        tensions={evaluation.tensions}
        risks={evaluation.risks}
        actions={evaluation.actions}
        openQuestions={evaluation.openQuestions}
        improvements={evaluation.improvements}
        judgeDetailPath={(judgeKey: string) => `${basePath}/judge/${judgeKey}/${evaluationId}`}
        homePath={`${basePath}/`}
        pitchPath={`${basePath}/pitch`}
        isPrescreen={evaluation.isPrescreen}
        onFeedbackSubmit={handleFeedbackSubmit}
        onPlayRecording={pitchSessionId ? video.handlePlayRecording : undefined}
        onOpenPitchDeck={pitchDeckId ? handleOpenPitchDeck : undefined}
        videoUrl={video.videoUrl}
        videoModalOpen={video.videoOpen}
        videoResumeTime={video.videoResumeTime}
        investorCard={investorCard}
      />

      {video.videoUrl && (
        <VideoPreviewModal
          videoUrl={video.videoUrl}
          title={`${evaluation.title}: Recording`}
          open={video.videoOpen}
          onOpenChange={video.setVideoOpen}
          startTime={video.videoStartTime}
          onTimeUpdate={video.setVideoResumeTime}
        />
      )}

      <DocumentPreviewModal
        document={docPreview.previewDoc}
        open={docPreview.previewOpen}
        onOpenChange={docPreview.setPreviewOpen}
        fetchUrl={docPreview.fetchDocumentUrl}
      />

      {contactDialog.contact && evaluation.investor && (
        <ContactMessageDialog
          open={contactDialog.open}
          onOpenChange={(open) => setContactDialog((s) => ({ ...s, open }))}
          contact={contactDialog.contact}
          startupId={startupId}
          investorId={evaluation.investor.id}
          investorName={evaluation.investor.name}
          websiteUrl={evaluation.investor.websiteUrl ?? null}
          evaluationContext={[
            evaluation.summary,
            evaluation.winnerJudge?.thesis
              ? `Investor thesis (what would make them fund): ${evaluation.winnerJudge.thesis}`
              : null,
            evaluation.tensions?.length
              ? `Key tensions: ${evaluation.tensions.map((t) => t.question).join('; ')}`
              : null,
            evaluation.actions?.length
              ? `Recommended actions: ${evaluation.actions
                  .slice(0, 3)
                  .map((a) => a.title)
                  .join('; ')}`
              : null,
          ]
            .filter(Boolean)
            .join('\n\n')}
          startupName={evaluation.startupName}
          savedBody={savedOutreachBody ?? outreachBodyQuery.data?.data?.outreachBody ?? null}
          onBodySaved={(body) => setSavedOutreachBody(body)}
          savedHook={hooksByContact[contactDialog.contact.id] ?? null}
          onHookSaved={(
            (contactId: string) => (hook: string) =>
              setHooksByContact((prev) => ({ ...prev, [contactId]: hook }))
          )(contactDialog.contact.id)}
          onOpenLinkedIn={() => {
            if (evaluation.investor?.id) logContactClick(evaluation.investor.id)
          }}
          source="evaluation"
          shareUrl={outreachBodyQuery.data?.data?.shareUrl ?? null}
        />
      )}
    </FounderLayout>
  )
}
